import { inject, ref } from 'vue';
import { ZoomPhoneResultApiService } from '@/api/user/resources/zoom_phone_result';
import { KEY_OF_MODAL } from '@/injection-keys';
import type { TModal } from '@/plugins/BModal';

export interface UseManualCallImportOptions {
  onImportSuccess?: (resultId: number, phoneNumber?: string) => void | Promise<void>;
}

type ImportResult = { resultId: number; phoneNumber?: string };

export const useManualCallImport = (options?: UseManualCallImportOptions) => {
  const modal = inject<TModal>(KEY_OF_MODAL);

  if (!modal) {
    throw new Error('BModal context not found');
  }

  const isLoading = ref(false);
  const errorMessage = ref<string>('');

  const openModal = () => {
    modal.show('manual-call-import-modal');
  };

  const closeModal = () => {
    modal.hide('manual-call-import-modal');
  };

  const executeImport = async (trimmedCallId: string): Promise<ImportResult> => {
    const api = new ZoomPhoneResultApiService();
    const response = await api.importZoomPhoneResult({
      request: { importZoomPhoneResultRequest: { callId: trimmedCallId } },
      errorHandlers: {
        400: (error: { response?: { data?: { errorMessage?: string } } }) => {
          const msg = error?.response?.data?.errorMessage;
          errorMessage.value = msg ? msg : 'zoomPhoneCallImportFailed';
        },
        404: (error) => {
          const msg = error?.response?.data?.errorMessage;
          errorMessage.value = msg ? msg : 'zoomPhoneCallImportFailed';
        },
      },
    });
    const zoomPhoneResult = response.data.zoomPhoneResult;
    return {
      resultId: zoomPhoneResult.id,
      phoneNumber: zoomPhoneResult.calleeDidNumber || undefined,
    };
  };

  const handleSubmit = async (callId: string) => {
    const trimmedCallId = callId.trim();
    isLoading.value = true;
    errorMessage.value = '';

    try {
      const result = await executeImport(trimmedCallId);
      await Promise.resolve(options?.onImportSuccess?.(result.resultId, result.phoneNumber));
      isLoading.value = false;
      closeModal();
    } catch (_error) {
      // ハンドリングされていないstatus code は errorHandlers で errorMessage をセット済み
    } finally {
      isLoading.value = false;
    }
  };

  return {
    openModal,
    closeModal,
    handleSubmit,
    isLoading,
    errorMessage,
  };
};
